import { X } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import PdfCanvasViewer from './PdfCanvasViewer'

interface DocumentViewerModalProps {
  isOpen: boolean
  onClose: () => void
  documentUrl: string
  documentName: string
}

export default function DocumentViewerModal({
  isOpen,
  onClose,
  documentUrl,
  documentName
}: DocumentViewerModalProps) {
  const { t } = useTranslation('common')
  if (!isOpen) return null

  const modalContent = (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">{documentName}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={t('close')}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* PDF Viewer */}
          <div className="p-4">
            <PdfCanvasViewer
              url={documentUrl}
              title={documentName}
              className="w-full"
              style={{ height: 'calc(100vh - 220px)', minHeight: 360 }}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t">
            <a
              href={documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              {t('openInNewTab')}
            </a>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
